// R12 — THE FRONT DOOR. THE FORM EVERY MEMBER FILLS IN TO EXIST ON THIS SITE.
//
// Fifteenth component out of App.tsx and the biggest hookless one left in it. It takes props, holds
// no state and calls no hook, so `scripts/r12-create-room.mjs` CALLS it and reads the tree back —
// and what that buys is not tidiness. This one form carries:
//
//   · T2-2's CONSENT CHECKBOX, and the law that the button stays SHUT until it is ticked. Until now
//     the only instrument on that was a single rendered puppeteer check of a single combination.
//   · the MIDDLE-NAME requirement numerology is built from (a married name silently entered here is
//     not a small error — the numbers are made of those exact letters).
//   · N19-1's two locations on one screen, which must never be confusable: where she was BORN and
//     where she LIVES NOW are different facts and stay different facts all the way down.
//   · the disabled / generating states, and whether a member who cannot submit is told WHY.
//
// It moved second, after `src/components/BirthFields.tsx`: a component cannot import back out of
// App.tsx, so the three field blocks it is built from had to be somewhere importable first. They
// hold state and it does not, which is exactly the R10-3 / R11-1 shape — the stubbed JSX runtime
// never CALLS a child, so their state costs this caller nothing.
//
// Moved VERBATIM.

import type { FormEvent } from "react";
import { NameFields, BirthTimeFields, BirthPlaceFields, UnknownTimeCheck, type BirthChartForm } from "./BirthFields";
import { CONSENT_TEXT } from "../termsConsent.js";
// C8b — the SMS opt-in sentence is DATA and is one declaration, so the three doors P3 adds cannot
// promise three different things.
import { SMS_CONSENT_TEXT } from "../memberPhone.js";
// 14-B2: the form's own copy DEFAULTS from the strings catalog against the language it is handed.
// Every word a caller passes still wins (R24: this copy arrives as props at a dozen call sites), and
// no language passed is English, so the ten reading forms that pass nothing are byte-identical.
import { DEFAULT_LANGUAGE } from "../language.js";
import { t } from "../strings.js";

export function CreateRoomForm({ birthChart, updateBirthChart, onSubmit, submitLabel, requireMiddleName = false, nameHint, className = "", disabled = false, disabledHint = "", generating = false, collectCurrentLocation = false, consent = null, phone = null, language = DEFAULT_LANGUAGE }: {
  birthChart: BirthChartForm;
  updateBirthChart: <K extends keyof BirthChartForm>(field: K, value: BirthChartForm[K]) => void;
  onSubmit: (event: FormEvent) => void;
  submitLabel?: string;
  requireMiddleName?: boolean;
  nameHint?: string;
  className?: string;
  // SP2: a two-person reading disables generate until the second person is chosen, with a warm hint.
  disabled?: boolean;
  disabledHint?: string;
  // AC3: opt IN to the shared pulsing generate treatment (.is-generating), so a room whose submit
  // IS a generate button glows while its work runs. Defaults to false, which leaves every room that
  // does not pass it byte-identical.
  generating?: boolean;
  // N19-1: SIGNUP ONLY. This is the one place a member tells us where she lives, and it is the one
  // place the two locations sit on the same screen, so it is the one place that needs the step
  // headings. Every reading form that casts a chart for someone stays exactly as it was.
  collectCurrentLocation?: boolean;
  // T2-2: SIGNUP ONLY. Passing this renders the mandatory consent checkbox immediately before the
  // button and holds the button shut until it is ticked. Every other caller of this form (editing
  // details, a reading form) passes nothing and is byte-identical to before.
  // 14-B2: HER LANGUAGE, and it is only ever a DEFAULT for the words above. Signup passes it; a
  // reading form that passes nothing keeps the English it always had.
  language?: string;
  consent?: null | {
    agreed: boolean;
    onChange: (agreed: boolean) => void;
    onOpenDoc: (doc: "terms" | "privacy" | "rules") => void;
    note?: string;
  };
  // C8b: SIGNUP ONLY, and it never blocks the button. Every other caller of this form passes
  // nothing and renders byte-identically to before — which is what keeps the reading forms, the
  // edit-details form and the wall's own create-room walk out of a round about the front door.
  phone?: null | {
    value: string;
    onChange: (value: string) => void;
    smsConsent: boolean;
    onSmsConsent: (agreed: boolean) => void;
    label: string;
    placeholder?: string;
    note?: string;
  };
}) {
  const consentBlocked = !!consent && !consent.agreed;
  const submitWords = submitLabel === undefined ? t(language, "form.submit") : submitLabel;
  const nameWords = nameHint === undefined ? t(language, "form.nameHint") : nameHint;
  return (
    <form className={`birth-chart-form${className ? ` ${className}` : ""}`} onSubmit={onSubmit}>
      {/* N19-1: on signup the two locations must never be confusable, so the birth details wear a
          heading that says AT BIRTH in words nobody can miss. Every other caller of this form is
          already unambiguously about one person's birth data and shows no heading at all. */}
      {collectCurrentLocation ? (
        <div className="form-step-head form-step-head--birth">
          <span className="form-step-head__num">1</span>
          <span className="form-step-head__title">{t(language, "form.step1.title")} <strong>{t(language, "form.step1.titleStrong")}</strong></span>
          <span className="form-step-head__sub">{t(language, "form.step1.sub")}</span>
        </div>
      ) : null}
      <div className="form-row form-row--name">
        <NameFields
          first={birthChart.firstName}
          middle={birthChart.middleName}
          last={birthChart.lastName}
          onChange={updateBirthChart}
          requireMiddle={requireMiddleName}
          language={language}
          noMiddle={birthChart.noMiddleName || false}
          onNoMiddle={(checked) => updateBirthChart("noMiddleName", checked)}
        />
        <p className="birth-chart-form__hint">{nameWords}</p>
      </div>
      <label className="form-field--date">
        <span>{t(language, "form.birthDate")}</span>
        <input type="date" value={birthChart.birthDate} onChange={(event) => updateBirthChart("birthDate", event.target.value)} required />
      </label>
      <div className="form-field form-field--time">
        <span>{t(language, "form.birthTime")}</span>
        <BirthTimeFields
          birthTime={birthChart.birthTime}
          birthPeriod={birthChart.birthPeriod}
          disabled={birthChart.birthTimeUnknown}
          language={language}
          onChange={(patch) => {
            if (patch.birthTime !== undefined) updateBirthChart("birthTime", patch.birthTime);
            if (patch.birthPeriod !== undefined) updateBirthChart("birthPeriod", patch.birthPeriod);
          }}
        />
        <UnknownTimeCheck checked={birthChart.birthTimeUnknown}
          onChange={(next) => updateBirthChart("birthTimeUnknown", next)}
          label={t(language, "form.birthTimeUnknown")} />
      </div>
      <BirthPlaceFields
        country={birthChart.birthCountry}
        birthplace={birthChart.birthplace}
        latitude={birthChart.latitude}
        longitude={birthChart.longitude}
        timezone={birthChart.timezone}
        birthDate={birthChart.birthDate}
        language={language}
        onChange={(patch) => {
          if (patch.birthCountry !== undefined) updateBirthChart("birthCountry", patch.birthCountry);
          if (patch.birthplace !== undefined) updateBirthChart("birthplace", patch.birthplace);
          if (patch.birthRegion !== undefined) updateBirthChart("birthRegion", patch.birthRegion);
          if (patch.latitude !== undefined) updateBirthChart("latitude", patch.latitude);
          if (patch.longitude !== undefined) updateBirthChart("longitude", patch.longitude);
          if (patch.timezone !== undefined) updateBirthChart("timezone", patch.timezone);
        }}
      />
      {/* Timezone resolves automatically from the birthplace; never shown as a field. */}

      {/* N19-1 STEP 2: WHERE SHE LIVES NOW. Its own step, its own heading, its own words, because
          the whole point of the ruling is that these two can never be taken for each other. Same
          field, same Geoapify search, same timezone resolution as the birth place above, but
          resolved against TODAY, since a current timezone has to be right about today's daylight
          saving rather than about the day she was born. */}
      {collectCurrentLocation ? (
        <>
          <div className="form-step-head form-step-head--current">
            <span className="form-step-head__num">2</span>
            <span className="form-step-head__title">{t(language, "form.step2.title")} <strong>{t(language, "form.step2.titleStrong")}</strong></span>
            <span className="form-step-head__sub">{t(language, "form.step2.sub")}</span>
          </div>
          <BirthPlaceFields
            country={birthChart.currentCountry || ""}
            birthplace={birthChart.currentPlace || ""}
            latitude={birthChart.currentLatitude || ""}
            longitude={birthChart.currentLongitude || ""}
            timezone={birthChart.currentTimezone || ""}
            birthDate={new Date().toISOString().slice(0, 10)}
            language={language}
            labels={{ country: t(language, "place.currentCountry"), city: t(language, "place.currentCity"), countryPlaceholder: t(language, "place.currentCountryPlaceholder"), cityPlaceholder: t(language, "place.currentCityPlaceholder") }}
            onChange={(patch) => {
              if (patch.birthCountry !== undefined) updateBirthChart("currentCountry", patch.birthCountry);
              if (patch.birthplace !== undefined) updateBirthChart("currentPlace", patch.birthplace);
              if (patch.birthRegion !== undefined) updateBirthChart("currentRegion", patch.birthRegion);
              if (patch.latitude !== undefined) updateBirthChart("currentLatitude", patch.latitude);
              if (patch.longitude !== undefined) updateBirthChart("currentLongitude", patch.longitude);
              if (patch.timezone !== undefined) updateBirthChart("currentTimezone", patch.timezone);
              // The IANA name is what her local midnight is measured against; the offset alone is
              // an hour wrong twice a year.
              if (patch.timezoneName !== undefined) updateBirthChart("currentTimezoneName", patch.timezoneName);
            }}
          />
        </>
      ) : null}

      <p className="birth-chart-form__privacy">
        {t(language, "form.privacy")}
      </p>
      {disabled && disabledHint ? <p className="birth-chart-form__gate">{disabledHint}</p> : null}

      {/* C8b — **HER PHONE NUMBER, OPTIONAL, WITH ITS OWN EXPLICIT UNCHECKED CONSENT.**
          It renders only where `phone` is supplied, which is the SIGNUP door — exactly as the
          consent block below does — so no other surface using this form grows a field.

          **(a) THE FIELD IS OPTIONAL AND SAYS SO ON ITS OWN LABEL.** `CreateRoomForm` is THE FRONT
          DOOR (R12) and this house's funnel is measurably working, so a required phone here is a
          conversion change she did not order, and a member who will not give one must still be able
          to exist here. If she wants it required that is one attribute and hers to say.

          **(b) THE OPT-IN IS ITS OWN UNCHECKED BOX AND IS NEVER IMPLIED BY THE FIELD.** C9 carries
          STOP-to-opt-out compliance; the other half of that law is that marketing SMS needs an
          opt-in the member actually gave. A number typed for account recovery is not permission to
          broadcast to it, and collapsing those two is what costs a sending NUMBER rather than a
          screen. **It cannot block the button**, because the number itself is optional and a
          consent to something optional cannot be a condition of entry.

          **(c) THE NUMBER IS NEVER RENDERED BACK ANYWHERE AND NEVER RIDES A MEASUREMENT RAIL**
          (VIBE-1 clause 3): what leaves this form is what she typed, and what is STORED is the
          normalised E.164 the server derives from it. */}
      {phone ? (
        <div className="phone-block">
          <label className="form-field form-field--phone">
            <span>{phone.label}</span>
            <input
              type="tel"
              name="phone"
              inputMode="tel"
              autoComplete="tel"
              value={phone.value}
              placeholder={phone.placeholder}
              onChange={(event) => phone.onChange(event.target.value)}
            />
          </label>
          {phone.note ? <p className="phone-block__note" role="alert">{phone.note}</p> : null}
          <label className="consent-check phone-block__consent">
            <input
              type="checkbox"
              name="smsConsent"
              aria-label={SMS_CONSENT_TEXT}
              checked={phone.smsConsent}
              onChange={(event) => phone.onSmsConsent(event.target.checked)}
            />
            <span className="consent-check__text">{SMS_CONSENT_TEXT}</span>
          </label>
        </div>
      ) : null}

      {/* T2-2 — THE CONSENT, IMMEDIATELY BEFORE THE BUTTON. Both documents open in a modal so she
          can read them without losing anything she has typed. */}
      {consent ? (
        <div className="consent-block">
          <label className="consent-check">
            <input
              type="checkbox"
              name="termsAgreed"
              aria-label={CONSENT_TEXT}
              checked={consent.agreed}
              onChange={(event) => consent.onChange(event.target.checked)}
            />
            <span className="consent-check__text">
              I have read and agree to the{" "}
              <button type="button" className="consent-link" onClick={() => consent.onOpenDoc("terms")}>Terms of Service</button>
              {" "}and{" "}
              <button type="button" className="consent-link" onClick={() => consent.onOpenDoc("privacy")}>Privacy Policy</button>
            </span>
          </label>
          {/* K4 (her J3-Q4 ruling, 2026-10-19): one line, sign-up only, no second checkbox. */}
          <p className="consent-agree">
            By signing up, you agree to our{" "}
            <button type="button" className="consent-agree__link" onClick={() => consent.onOpenDoc("terms")}>Terms</button>
            {" "}and{" "}
            <button type="button" className="consent-agree__link" onClick={() => consent.onOpenDoc("rules")}>house rules</button>.
          </p>
          {consent.note ? <p className="consent-block__note" role="alert">{consent.note}</p> : null}
        </div>
      ) : null}

      <button className={`button button--primary${generating ? " is-generating" : ""}`} type="submit" disabled={disabled || generating || consentBlocked}>{submitWords}</button>
    </form>
  );
}
